import React, { useState, useEffect, useMemo, useRef } from "react";
import { yogaApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./Yoga.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const BLANK_INFO = {
  _id: null,
  Title: "",
  Subtitle: "",
  Description: "",
  Location: "",
  Capacity: "",
  Timing: "",
  Established_Year: "",
  In_Charge: "",
  Email: "",
  Phone: ""
};

const BLANK_FACILITY = { 
  _id: null, 
  Facility_Name: "", 
  Description: "" 
};

const Yoga = () => {
  const [activeTab, setActiveTab]           = useState("overview");
  const [pageInfo, setPageInfo]             = useState(BLANK_INFO);
  const [facilities, setFacilities]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [facilityForm, setFacilityForm]     = useState(BLANK_FACILITY);
  const [confirmId, setConfirmId]           = useState(null);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [infoRes, facRes] = await Promise.all([
        apiClient(`${yogaApi}/page-info`),
        apiClient(`${yogaApi}/facilities`)
      ]);
      const infoResult = await infoRes.json();
      const facResult = await facRes.json();
      if (infoRes.ok && infoResult.data && Object.keys(infoResult.data).length > 0) {
        setPageInfo(infoResult.data);
      }
      if (facRes.ok) setFacilities(facResult.data || []);
    } catch (error) {
      console.error("Error fetching yoga data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 190 });
    setDropdownOpenId(id);
  };

  const handleInfoSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient(`${yogaApi}/page-info`, { 
        method: "POST", 
        body: JSON.stringify(pageInfo) 
      });
      if (response.ok) fetchData();
    } catch (error) {
      console.error("Error saving info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFacilitySave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? `${yogaApi}/facilities` : `${yogaApi}/facilities/${facilityForm._id}`;
      const response = await apiClient(url, { 
        method, 
        body: JSON.stringify(facilityForm) 
      });
      if (response.ok) {
        fetchData(); 
        setModalType("");
      }
    } catch (error) {
      console.error("Error saving facility:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFacilityDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${yogaApi}/facilities/${id}`, { method: "DELETE" });
      if (response.ok) {
        fetchData(); 
        setConfirmId(null); 
        setDropdownOpenId(null);
      }
    } catch (error) {
      console.error("Error deleting facility:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="yoga-page">
      {/* ── Header ── */}
      <div className="yoga-header">
        <div className="yoga-header-left">
          <div className="yoga-header-icon">🧘</div>
          <div>
            <div className="yoga-breadcrumb">
              <span>Facilities</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="yoga-bc-active">Yoga & Wellness</span>
            </div>
            <h1>Yoga & Wellness</h1>
            <p>Manage yoga centre information, physical facilities, and holistic wellness programs.</p>
          </div>
        </div>
        <div className="yoga-tabs">
          <button className={`yoga-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
          <button className={`yoga-tab ${activeTab === "facilities" ? "active" : ""}`} onClick={() => setActiveTab("facilities")}>Facilities</button>
          <button className={`yoga-tab ${activeTab === "settings" ? "active" : ""}`} onClick={() => setActiveTab("settings")}>Centre Details</button>
        </div>
      </div>

      {loading && <div className="loading-overlay">Syncing wellness data...</div>}

      {/* ── Tab: Overview ── */}
      {activeTab === "overview" && (
        <div className="yoga-overview-tab">
          <div className="yoga-preview-banner">
            <div className="ypb-left">
              <span className="ypb-badge">Wellness Centre</span>
              <h2>{pageInfo.Title || "Yoga & Wellness Centre"}</h2>
              <p className="ypb-subtitle">{pageInfo.Subtitle || "Cultivating Inner Peace & Vitality"}</p>
              <p className="ypb-desc">{pageInfo.Description || "The college yoga centre provides a serene environment for students and staff to achieve physical and mental well-being through professional guidance."}</p>
              <div className="ypb-meta-row">
                <span>📍 {pageInfo.Location || 'TBD'}</span>
                <span>👥 {pageInfo.Capacity || '0'} Capacity</span>
                <span>⏰ {pageInfo.Timing || 'TBD'}</span>
              </div>
            </div>
            <div className="ypb-right">
              <div className="ypb-stats-grid">
                <div><strong>{facilities.length}</strong><span>Active Zones</span></div>
                <div><strong>{pageInfo.Established_Year || "—"}</strong><span>Since</span></div>
              </div>
            </div>
          </div>
          <div className="yoga-info-cards">
            <div className="yoga-info-card">
              <div className="yoga-ic-icon">👤</div>
              <div><span className="yoga-ic-label">In-Charge</span><span className="yoga-ic-val">{pageInfo.In_Charge || "Not Assigned"}</span></div>
            </div>
            <div className="yoga-info-card">
              <div className="yoga-ic-icon">📧</div>
              <div><span className="yoga-ic-label">Email</span><span className="yoga-ic-val">{pageInfo.Email || "info@college.edu"}</span></div>
            </div>
            <div className="yoga-info-card">
              <div className="yoga-ic-icon">📞</div>
              <div><span className="yoga-ic-label">Phone</span><span className="yoga-ic-val">{pageInfo.Phone || "+91 00000 00000"}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Facilities ── */}
      {activeTab === "facilities" && (
        <div className="yoga-overview-tab">
          <div className="yoga-section-head">
            <div>
              <h2>Centre Facilities & Zones</h2>
              <p>Physical areas and specialized equipment available for wellness practices.</p>
            </div>
            <button className="yoga-btn-primary" onClick={() => { setFacilityForm(BLANK_FACILITY); setModalType("add"); }}>
              + Add New Facility
            </button>
          </div>
          <div className="yoga-fac-grid">
            {facilities.map(fac => (
              <div key={fac._id} className="yoga-fac-card">
                <div className="yoga-fac-head">
                  <div className="yoga-fac-icon">🌿</div>
                  <h3>{fac.Facility_Name}</h3>
                  <button className="yoga-dots" onClick={e => handleDotsClick(e, fac._id)}>⋮</button>
                </div>
                <p>{fac.Description}</p>
              </div>
            ))}
            {facilities.length === 0 && <div className="sh-empty" style={{ gridColumn: '1 / -1' }}><h3>No facilities listed yet.</h3><p>Click 'Add New Facility' to populate your centre profile.</p></div>}
          </div>
        </div>
      )}

      {/* ── Tab: Settings ── */}
      {activeTab === "settings" && (
        <div className="yoga-overview-tab">
          <div className="yoga-section-head">
            <div>
              <h2>Manage Centre Information</h2>
              <p>Update main titles, location details, and staff contact information.</p>
            </div>
          </div>
          <form className="is-form" onSubmit={handleInfoSave}>
            <div className="is-field full"><label>Centre Title</label><input value={pageInfo.Title || ""} onChange={e => setPageInfo({ ...pageInfo, Title: e.target.value })} placeholder="e.g. Maharishi Patanjali Yoga Centre" /></div>
            <div className="is-field full"><label>Subtitle / Motto</label><input value={pageInfo.Subtitle || ""} onChange={e => setPageInfo({ ...pageInfo, Subtitle: e.target.value })} placeholder="e.g. Harmony of Mind, Body and Soul" /></div>
            <div className="is-field full"><label>Detailed Description</label><textarea value={pageInfo.Description || ""} onChange={e => setPageInfo({ ...pageInfo, Description: e.target.value })} rows="3" placeholder="Describe the centre's mission and regular activities..." /></div>
            <div className="is-field-row">
              <div className="is-field"><label>Specific Location</label><input value={pageInfo.Location || ""} onChange={e => setPageInfo({ ...pageInfo, Location: e.target.value })} placeholder="e.g. Main Block, Third Floor" /></div>
              <div className="is-field"><label>Capacity (People)</label><input value={pageInfo.Capacity || ""} onChange={e => setPageInfo({ ...pageInfo, Capacity: e.target.value })} placeholder="e.g. 50 Practitioners" /></div>
            </div>
            <div className="is-field-row">
              <div className="is-field"><label>Head / In-Charge</label><input value={pageInfo.In_Charge || ""} onChange={e => setPageInfo({ ...pageInfo, In_Charge: e.target.value })} placeholder="e.g. Dr. Ramesh Gupta" /></div>
              <div className="is-field"><label>Operational Timing</label><input value={pageInfo.Timing || ""} onChange={e => setPageInfo({ ...pageInfo, Timing: e.target.value })} placeholder="e.g. 06:00 AM - 08:00 PM" /></div>
            </div>
            <div className="is-field-row">
              <div className="is-field"><label>Centre Email</label><input type="email" value={pageInfo.Email || ""} onChange={e => setPageInfo({ ...pageInfo, Email: e.target.value })} placeholder="e.g. yoga@college.edu" /></div>
              <div className="is-field"><label>Contact Number</label><input value={pageInfo.Phone || ""} onChange={e => setPageInfo({ ...pageInfo, Phone: e.target.value })} placeholder="e.g. +91 98765 43210" /></div>
            </div>
            <div className="is-field-row">
              <div className="is-field"><label>Established Year</label><input value={pageInfo.Established_Year || ""} onChange={e => setPageInfo({ ...pageInfo, Established_Year: e.target.value })} placeholder="e.g. 2018" /></div>
            </div>
            <div className="is-form-footer" style={{ marginTop: 10 }}>
              <button type="submit" className="yoga-btn-primary">Update Centre Profile</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Dropdown ── */}
      {dropdownOpenId && (
        <div ref={dropdownRef} className="sh-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          <button onClick={() => { setFacilityForm(facilities.find(f => f._id === dropdownOpenId)); setModalType("edit"); setDropdownOpenId(null); }}>Edit Facility</button>
          <button className="danger" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Record</button>
        </div>
      )}

      {/* ── Modals ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="is-overlay" onClick={() => setModalType("")}>
          <div className="sh-modal" style={{ maxWidth: '540px' }} onClick={e => e.stopPropagation()}>
            <div className="sh-modal-head" style={{ border: 'none', padding: 0, marginBottom: 20 }}>
              <div><h2>{modalType === "add" ? "Add" : "Edit"} Facility</h2><p>Define a new activity zone or equipment category for the centre.</p></div>
              <button className="sh-close" onClick={() => setModalType("")}>×</button>
            </div>
            <form className="sh-form" onSubmit={handleFacilitySave}>
              <div className="sh-field full"><label>Facility Name *</label><input value={facilityForm.Facility_Name || ""} onChange={e => setFacilityForm({ ...facilityForm, Facility_Name: e.target.value })} placeholder="e.g. Meditation Hall" required /></div>
              <div className="sh-field full"><label>Short Description</label><textarea value={facilityForm.Description || ""} onChange={e => setFacilityForm({ ...facilityForm, Description: e.target.value })} rows="3" placeholder="Describe the purpose or equipment available..." /></div>
              <div className="sh-actions" style={{ marginTop: 8 }}>
                <button type="button" className="btn-light" onClick={() => setModalType("")}>Cancel</button>
                <button type="submit" className="btn-primary">Save Facility Record</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ── */}
      {confirmId && (
        <div className="is-overlay" onClick={() => setConfirmId(null)}>
          <div className="sh-modal" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to permanently remove this facility? Students will no longer see it listed under the centre.</p>
            <div className="sh-actions">
              <button className="btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleFacilityDelete(confirmId)}>Yes, Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Yoga;